import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Stack,
  Paper,
  Typography,
  Breadcrumbs,
  TextField,
  InputAdornment,
  Button,
  Chip,
  CircularProgress,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import CallIcon from "@mui/icons-material/Call";
import CloudOffIcon from "@mui/icons-material/CloudOffOutlined";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchOfflineCompanies, setQuery } from "./offlineSlice";

const fmtDateTime = (v) =>
  v ? dayjs(v).format("DD-MM-YYYY hh:mm A") : "No update";

const fmtDuration = (v) => {
  if (!v) return "—";
  const ms = Date.now() - new Date(v).getTime();
  if (ms < 0) return "just now";
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  const hours = Math.floor((ms % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const mins = Math.floor((ms % (60 * 60 * 1000)) / (60 * 1000));
  if (days > 0) return `${days}d ${hours}h ago`;
  if (hours > 0) return `${hours}h ${mins}m ago`;
  return `${mins}m ago`;
};

const telHref = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : null;
};

// Same palette + 3-row contact cell used in Companies/Search/Tickets so the
// Offline page renders contacts identically.
const CONTACT_COLORS = {
  operator:    { idle: "#a78bfa", hover: "#8b5cf6" },
  electrician: { idle: "#fbbf24", hover: "#f59e0b" },
  secretary:   { idle: "#34d399", hover: "#10b981" },
};

const TableContactCell = ({ c }) => {
  const rows = [
    { label: "Operator",    name: c.operator_name,     phone: c.operator_phone,           colors: CONTACT_COLORS.operator },
    { label: "Electrician", name: null,                phone: c.local_electrician_number, colors: CONTACT_COLORS.electrician },
    { label: "Secretary",   name: c.secretary_name,    phone: c.secretary_phone,          colors: CONTACT_COLORS.secretary },
  ];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0 }}>
      {rows.map((r) => {
        const callable = Boolean(r.phone);
        return (
          <Box
            key={r.label}
            onClick={callable ? (e) => e.stopPropagation() : undefined}
            component={callable ? "a" : "div"}
            href={callable ? telHref(r.phone) : undefined}
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "20px 78px minmax(0, 1fr) auto",
                md: "20px 90px 160px 120px",
              },
              alignItems: "center",
              columnGap: 1.25,
              borderRadius: "4px",
              py: 0.25,
              minWidth: 0,
              textDecoration: "none",
              color: "inherit",
              ...(callable && {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  "& .row-call-btn": { backgroundColor: r.colors.hover },
                },
              }),
            }}
          >
            <Box
              className="row-call-btn"
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: callable ? r.colors.idle : "rgba(0,0,0,0.08)",
                transition: "background-color 0.15s",
                "& .MuiSvgIcon-root": {
                  color: callable ? "#ffffff" : "rgba(0,0,0,0.3)",
                  fontSize: 11,
                },
              }}
            >
              <CallIcon />
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6875rem",
                fontWeight: 600,
                color: "text.secondary",
                whiteSpace: "nowrap",
              }}
            >
              {r.label}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6875rem",
                color: r.name ? "text.primary" : "text.disabled",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {r.name || "—"}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6875rem",
                fontFamily: "monospace",
                fontWeight: 500,
                color: r.phone ? "text.primary" : "text.disabled",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {r.phone || "—"}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default function OfflineCompaniesPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { items, pagination, meta, query, loading, refreshedAt } = useSelector(
    (s) => s.offline
  );

  const [searchInput, setSearchInput] = useState("");
  const [hoursInput, setHoursInput] = useState(query.hours);
  const [loadingMore, setLoadingMore] = useState(false);
  const debounceRef = useRef(null);
  const sentinelRef = useRef(null);

  // Initial load
  useEffect(() => {
    dispatch(fetchOfflineCompanies(query));
    // eslint-disable-next-line
  }, []);

  // Debounced search
  const onSearchInput = (v) => {
    setSearchInput(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      dispatch(setQuery({ search: v.trim(), page: 1 }));
    }, 400);
  };

  // Hours filter — submitted on blur or Enter
  const applyHours = () => {
    const next = Math.max(parseInt(hoursInput, 10) || 1, 1);
    if (next !== query.hours) {
      dispatch(setQuery({ hours: next, page: 1 }));
    }
  };

  // Refetch whenever query changes (after mount)
  useEffect(() => {
    dispatch(fetchOfflineCompanies(query));
    // eslint-disable-next-line
  }, [query]);

  // Mobile infinite scroll
  const loadMoreRef = useRef();
  loadMoreRef.current = async () => {
    if (loadingMore || loading) return;
    if (pagination.page >= pagination.pages) return;
    setLoadingMore(true);
    try {
      await dispatch(
        fetchOfflineCompanies({
          ...query,
          page: pagination.page + 1,
          append: true,
        })
      );
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (!isMobile) return;
    const node = sentinelRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMoreRef.current?.();
      },
      { rootMargin: "200px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [isMobile, items.length, pagination.page, pagination.pages]);

  const refresh = () => {
    dispatch(fetchOfflineCompanies(query));
  };

  const openCompany = (companyId) => {
    if (!companyId) return;
    navigate(`/companies/${companyId}/details`);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: { md: "100%" },
        minHeight: 0,
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={1}
        mb={1.5}
        sx={{ flexShrink: 0 }}
      >
        <Breadcrumbs
          separator="›"
          sx={{ fontSize: "0.9375rem", display: { xs: "none", sm: "flex" } }}
        >
          <Typography color="text.secondary" sx={{ fontSize: "0.9375rem" }}>
            Home
          </Typography>
          <Typography
            color="text.primary"
            sx={{ fontSize: "0.9375rem", fontWeight: 600 }}
          >
            Offline Companies
          </Typography>
        </Breadcrumbs>

        {/* Stat badges + refresh */}
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <Chip
            icon={<CloudOffIcon sx={{ fontSize: 14 }} />}
            label={`${meta?.offlineCount ?? 0} offline`}
            size="small"
            sx={{
              height: 22,
              fontSize: "0.6875rem",
              fontWeight: 700,
              backgroundColor: "#fee2e2",
              color: "#b91c1c",
            }}
          />
          {meta?.totalLiveCompanies != null && (
            <Chip
              label={`of ${meta.totalLiveCompanies} live`}
              size="small"
              sx={{
                height: 22,
                fontSize: "0.6875rem",
                fontWeight: 600,
                backgroundColor: "action.hover",
              }}
            />
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={
              loading ? (
                <CircularProgress size={12} />
              ) : (
                <RefreshIcon sx={{ fontSize: 16 }} />
              )
            }
            onClick={refresh}
            disabled={loading}
            sx={{ fontSize: "0.75rem" }}
          >
            {loading ? "Refreshing…" : "Refresh"}
          </Button>
        </Stack>
      </Stack>

      <Paper
        variant="outlined"
        sx={{
          overflow: "hidden",
          flex: { md: 1 },
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* === Toolbar === */}
        <Box
          sx={{
            p: 1,
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
          }}
        >
          <Stack
            direction="row"
            spacing={1}
            sx={{ flexWrap: "wrap", rowGap: 1, alignItems: "center" }}
            useFlexGap
          >
            <TextField
              size="small"
              placeholder={
                isMobile
                  ? "Search…"
                  : "Search by name, district, mfg id, IMEI…"
              }
              value={searchInput}
              onChange={(e) => onSearchInput(e.target.value)}
              sx={{ flex: "1 1 220px", minWidth: 0 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchInput && (
                  <InputAdornment position="end">
                    <Button
                      size="small"
                      onClick={() => {
                        setSearchInput("");
                        dispatch(setQuery({ search: "", page: 1 }));
                      }}
                      sx={{ minWidth: 0, p: 0.25 }}
                    >
                      Clear
                    </Button>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              type="number"
              label="Stale after (hours)"
              value={hoursInput}
              onChange={(e) => setHoursInput(e.target.value)}
              onBlur={applyHours}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  applyHours();
                }
              }}
              inputProps={{ min: 1 }}
              sx={{
                flex: { xs: "0 1 140px", md: "0 1 160px" },
                minWidth: 0,
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                flexShrink: 0,
                fontSize: "0.7rem",
                display: { xs: "none", md: "block" },
              }}
            >
              Last checked:{" "}
              {refreshedAt ? dayjs(refreshedAt).format("hh:mm A") : "—"}
            </Typography>
          </Stack>
        </Box>

        {/* === Pagination (desktop only, above the table) === */}
        <Stack
          direction="row"
          spacing={0.75}
          sx={{
            p: 1,
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "divider",
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Showing {items.length} of {pagination.total} · Page{" "}
            {pagination.page} of {Math.max(pagination.pages, 1)}
          </Typography>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Pagination
              size="small"
              page={pagination.page}
              count={Math.max(pagination.pages, 1)}
              onChange={(_, p) => dispatch(setQuery({ page: p }))}
              shape="rounded"
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
        </Stack>

        {/* === List / table === */}
        {isMobile ? (
          <Box
            sx={{
              p: 1,
              backgroundColor: "background.default",
              flex: 1,
              overflow: "auto",
              minHeight: 0,
            }}
          >
            {loading && items.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!loading && items.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                <Typography variant="caption" display="block">
                  No offline live companies in the last {query.hours} hour
                  {query.hours === 1 ? "" : "s"}.
                </Typography>
              </Box>
            )}
            <Stack spacing={0.75}>
              {items.map((c) => {
                const labelSx = {
                  fontSize: "0.6875rem",
                  color: "text.secondary",
                  fontWeight: 600,
                  minWidth: 70,
                  flexShrink: 0,
                };
                const valueSx = {
                  fontSize: "0.6875rem",
                  color: "text.primary",
                  wordBreak: "break-word",
                };
                const location = [c.district, c.block, c.panchayat]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <Paper
                    key={c._id}
                    variant="outlined"
                    onClick={() => openCompany(c._id)}
                    sx={{
                      p: 1.25,
                      cursor: "pointer",
                      minWidth: 0,
                      overflow: "hidden",
                      // Same light surface as Companies / Search / Tickets.
                      backgroundColor: "#f4f8fc",
                      borderColor: "#dbe6f0",
                      transition: "background-color .15s, border-color .15s",
                      "&:hover": {
                        backgroundColor: "#e8f0fa",
                        borderColor: "primary.main",
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={0.5}
                      mb={0.75}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {c.manufacturing_id != null && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: "monospace",
                            color: "primary.main",
                            fontSize: "0.6875rem",
                            fontWeight: 700,
                          }}
                        >
                          #{c.manufacturing_id}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          fontSize: "0.8125rem",
                          color: "text.primary",
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {c.name || "—"}
                      </Typography>
                      <Chip
                        label={fmtDuration(c.lastUpdatedAt)}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.625rem",
                          fontWeight: 700,
                          backgroundColor: "#fee2e2",
                          color: "#b91c1c",
                        }}
                      />
                    </Stack>
                    <Stack spacing={0.4}>
                      <Stack direction="row" spacing={1}>
                        <Typography sx={labelSx}>Location</Typography>
                        <Typography sx={valueSx}>{location || "—"}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Typography sx={labelSx}>Last update</Typography>
                        <Typography sx={valueSx}>
                          {c.lastUpdatedAt ? dayjs(c.lastUpdatedAt).format("DD-MM-YYYY") : "—"}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Typography sx={labelSx}>Last IMEI</Typography>
                        <Typography
                          sx={{ ...valueSx, fontFamily: "monospace" }}
                        >
                          {c.lastUpdatedImei ||
                            c.motor_imei ||
                            c.oht_imei ||
                            "—"}
                        </Typography>
                      </Stack>
                    </Stack>
                    {/* Same compact 3-row contact block (Operator / Electrician
                        / Secretary) used in every other mobile card. */}
                    <Box sx={{ mt: 0.5, pt: 0.5, borderTop: "1px solid", borderColor: "divider" }}>
                      <TableContactCell c={c} />
                    </Box>
                  </Paper>
                );
              })}
            </Stack>

            {items.length > 0 && (
              <Box
                ref={sentinelRef}
                sx={{ py: 2, textAlign: "center", color: "text.secondary" }}
              >
                {loadingMore ? (
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    justifyContent="center"
                  >
                    <CircularProgress size={16} />
                    <Typography variant="caption">Loading more…</Typography>
                  </Stack>
                ) : pagination.page < pagination.pages ? (
                  <Typography variant="caption">Scroll for more</Typography>
                ) : (
                  <Typography
                    variant="caption"
                    sx={{ color: "text.disabled" }}
                  >
                    All {pagination.total} offline companies loaded
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                // Fixed layout so the inner grid in the Contact cell lines
                // up vertically across rows (same fix as Companies / Search
                // / Tickets).
                tableLayout: "fixed",
                "& tbody tr:last-child .MuiTableCell-root": {
                  borderBottom: "1px solid",
                  borderColor: "divider",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 40 }}>#</TableCell>
                  <TableCell sx={{ width: 80 }}>Mfg ID</TableCell>
                  <TableCell sx={{ width: 180 }}>Name</TableCell>
                  <TableCell sx={{ width: 470 }}>Contact</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell sx={{ width: 130, whiteSpace: "nowrap" }}>
                    Last update
                  </TableCell>
                  <TableCell sx={{ width: 110, whiteSpace: "nowrap" }}>
                    Stale since
                  </TableCell>
                  <TableCell sx={{ width: 150 }}>Last IMEI</TableCell>
                  <TableCell sx={{ width: 80 }} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                      <CircularProgress size={20} />
                    </TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      align="center"
                      sx={{ py: 4, color: "text.secondary" }}
                    >
                      No offline live companies in the last {query.hours} hour
                      {query.hours === 1 ? "" : "s"}.
                    </TableCell>
                  </TableRow>
                )}
                {items.map((c, i) => (
                  <TableRow
                    key={c._id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() => openCompany(c._id)}
                  >
                    <TableCell sx={{ color: "text.secondary" }}>
                      {(pagination.page - 1) * pagination.limit + i + 1}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        color: "primary.main",
                        fontWeight: 700,
                      }}
                    >
                      {c.manufacturing_id != null
                        ? `#${c.manufacturing_id}`
                        : "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.8125rem",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {c.name || "—"}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TableContactCell c={c} />
                    </TableCell>
                    <TableCell
                      sx={{ fontSize: "0.7rem", color: "text.secondary" }}
                    >
                      {[c.district, c.block, c.panchayat]
                        .filter(Boolean)
                        .join(" · ") || "—"}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: "0.7rem",
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {fmtDateTime(c.lastUpdatedAt)}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={fmtDuration(c.lastUpdatedAt)}
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: "0.6875rem",
                          fontWeight: 700,
                          backgroundColor: "#fee2e2",
                          color: "#b91c1c",
                          borderRadius: "2px",
                        }}
                      />
                    </TableCell>
                    <TableCell
                      sx={{
                        fontFamily: "monospace",
                        fontSize: "0.7rem",
                        color: "text.secondary",
                      }}
                    >
                      {c.lastUpdatedImei ||
                        c.motor_imei ||
                        c.oht_imei ||
                        "—"}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          openCompany(c._id);
                        }}
                        sx={{ fontSize: "0.6875rem", py: 0.25, minHeight: 0 }}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
